// MirrorActionChip: inline confirmation card for Mirror action proposals.
// Rendered immediately after an assistant message whose response carried a
// tool_use proposal. Glassmorphism styling matches NudgeCard / MirrorChat overlay.

import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Brain,
  Calendar,
  CheckSquare,
  Download,
  FileText,
  Check,
  Bell,
  Loader2,
  Sparkles,
} from "lucide-react";
import { MIRROR_LANGUAGE_TUTOR_HANDOFF_ENABLED, MIRROR_BODY_HEALTH_CARDS_ENABLED, MIRROR_SLEEP_CARDS_ENABLED } from "@/lib/featureFlags";
import { useLanguage } from "@/i18n/LanguageProvider";
import { localeTagFor, type Lang } from "@/i18n/locale";
import type { ActionProposal } from "@/types/mirror";

type ChipStatus = "pending" | "created" | "undone";

interface MirrorActionChipProps {
  proposal: ActionProposal;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
  onUndo?: () => Promise<void>;
  // F1 PR-D: entregável de ARQUIVO gerado → o chip criado oferece BAIXAR.
  // O handler (MirrorChat) usa o util compartilhado downloadFile.ts; o chip
  // só renderiza a affordance.
  onDownload?: () => Promise<void>;
  disabled?: boolean;
  className?: string;
}

const UNDO_WINDOW_MS = 10_000;

function formatDateTime(iso: string, lang: Lang): string {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return new Intl.DateTimeFormat(localeTagFor(lang), {
      weekday: "short",
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    }).format(d);
  } catch {
    return iso;
  }
}

function interpolate(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, key) =>
    values[key] != null ? String(values[key]) : `{${key}}`,
  );
}

function iconForTool(tool: ActionProposal["tool"]) {
  if (tool === "propose_brain_note_action" || tool === "prepare_brain_handoff") return Brain;
  if (tool === "create_agenda_event") return Calendar;
  if (tool === "create_task" || tool === "create_habit" || tool === "propose_habit_action" || tool === "propose_habit_log") return CheckSquare;
  if (tool === "propose_deliverable") return Sparkles;
  return FileText;
}

function titleKey(tool: ActionProposal["tool"]) {
  if (tool === "propose_connected_message_action") return "connectedMessageSend.title";
  if (tool === "propose_connected_task_action") return "connectedTask.title";
  if (tool === "propose_finance_action" || tool === "propose_finance_log") return "financeAction.title";
  if (tool === "propose_brain_note_action") return "brainAction.title";
  if (tool === "prepare_brain_handoff") return "brainAction.handoffTitle";
  if (tool === "create_agenda_event") return "mirror.actions.createEvent.title";
  if (tool === "create_task") return "mirror.actions.createTask.title";
  if (tool === "create_habit" || tool === "propose_habit_action" || tool === "propose_habit_log") return "habitAction.title";
  if (tool === "propose_deliverable") return "mirror.actions.deliverable.title";
  if (tool === "propose_body_log" || tool === "propose_body_health_action") return "bodyHealthAction.title";
  if (tool === "propose_nutrition_meal_action") return "nutritionMealAction.title";
  if (tool === "propose_fitness_curated_plan") return "fitnessCuratedPlanAction.title";
  if (tool === "propose_language_class_action") return "languageClassAction.title";
  if (tool === "prepare_language_tutor_handoff") return "languageClassAction.tutorTitle";
  if (tool === "propose_language_action") return "languageAction.title";
  if (tool === "propose_nutrition_goals_action") return "nutritionGoalsAction.title";
  if (tool === "propose_nutrition_hydration_action") return "nutritionHydrationAction.title";
  if (tool === "propose_fitness_action") return "fitnessAction.title";
  if (tool === "propose_fitness_draft_restore") return "mirror.fitnessDraftRestore.title";
  if (tool === "propose_fitness_workout_finish") return "fitnessWorkoutFinish.title";
  if (tool === "propose_fitness_saved_plan_prepare") return "fitnessSavedPlanPrepare.title";
  if (tool === "propose_fitness_rest_skip") return "fitnessRestSkip.title";
  if (tool === "propose_fitness_current_set_completion") return "fitnessCurrentSetCompletion.title";
  if (tool === "propose_fitness_setup_start") return "fitnessSetupStart.title";
  if (tool === "propose_fitness_current_set_edit") return "fitnessCurrentSetEdit.title";
  if (tool === "propose_sleep_log" || tool === "propose_sleep_action") return "sleepAction.title";
  return "mirror.actions.createNote.title";
}

const MirrorActionChip = ({
  proposal,
  onConfirm,
  onCancel,
  onUndo,
  onDownload,
  disabled,
  className,
}: MirrorActionChipProps) => {
  const { t, lang } = useLanguage();
  const [status, setStatus] = useState<ChipStatus>("pending");
  const [submitting, setSubmitting] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [showUndo, setShowUndo] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (status !== "created") return;
    setShowUndo(true);
    const timer = window.setTimeout(() => setShowUndo(false), UNDO_WINDOW_MS);
    return () => window.clearTimeout(timer);
  }, [status]);

  const isSleepReview = proposal.tool === "propose_sleep_log" || proposal.tool === "propose_sleep_action";
  const isBodyHealthReview = proposal.tool === "propose_body_log" || proposal.tool === "propose_body_health_action";
  const isFitnessReview = proposal.tool === "propose_fitness_action";
  const isRestorePreview = proposal.tool === "propose_fitness_draft_restore";
  const isWorkoutFinishPreview = proposal.tool === "propose_fitness_workout_finish";
  const isSavedPlanPreparePreview = proposal.tool === "propose_fitness_saved_plan_prepare";
  const isRestSkipPreview = proposal.tool === "propose_fitness_rest_skip";
  const isSetCompletionPreview = proposal.tool === "propose_fitness_current_set_completion";
  const isSetupStartPreview = proposal.tool === "propose_fitness_setup_start";
  const isSetEditPreview = proposal.tool === "propose_fitness_current_set_edit";
  const isFinanceReview = proposal.tool === "propose_finance_action" || proposal.tool === "propose_finance_log";
  const isReviewedLocalAction = proposal.tool === "propose_connected_message_action" || proposal.tool === "propose_connected_task_action" || isFinanceReview || isSleepReview || isBodyHealthReview || isFitnessReview || proposal.tool === 'propose_nutrition_meal_action' || proposal.tool === 'propose_nutrition_hydration_action' || proposal.tool === 'propose_language_action' || proposal.tool === 'propose_language_class_action' || proposal.tool === 'prepare_language_tutor_handoff' || proposal.tool === 'propose_brain_note_action' || proposal.tool === 'prepare_brain_handoff' || proposal.tool === 'propose_nutrition_goals_action' || proposal.tool === 'propose_fitness_curated_plan';
  const sleepDisabled = proposal.tool === "prepare_language_tutor_handoff" && !MIRROR_LANGUAGE_TUTOR_HANDOFF_ENABLED || isSleepReview && !MIRROR_SLEEP_CARDS_ENABLED || isBodyHealthReview && !MIRROR_BODY_HEALTH_CARDS_ENABLED;
  const Icon = iconForTool(proposal.tool);

  const previewNode = useMemo(() => {
    if (proposal.tool === "propose_connected_message_action") return <p className="text-sm whitespace-pre-wrap break-words">{t("connectedMessageSend.reviewHint")}</p>;
    if (proposal.tool === "propose_connected_task_action") return <p className="text-sm whitespace-pre-wrap break-words">{t("connectedTask.reviewHint")}</p>;
    if (proposal.tool === "propose_finance_action" || proposal.tool === "propose_finance_log") return <p className="text-sm whitespace-pre-wrap break-words">{t("financeAction.mirrorReview")}</p>;
    if (proposal.tool === "propose_fitness_workout_finish") return <p className="text-sm whitespace-pre-wrap break-words">{t("fitnessWorkoutFinish.changed")}</p>;
    if (proposal.tool === "propose_fitness_saved_plan_prepare") return <p className="text-sm whitespace-pre-wrap break-words">{t("fitnessSavedPlanPrepare.changed")}</p>;
    if (proposal.tool === "propose_fitness_rest_skip") return <p className="text-sm whitespace-pre-wrap break-words">{t("fitnessRestSkip.changed")}</p>;
    if (proposal.tool === "propose_fitness_current_set_completion") return <p className="text-sm whitespace-pre-wrap break-words">{t("fitnessCurrentSetCompletion.changed")}</p>;
    if (proposal.tool === "propose_fitness_setup_start") return <p className="text-sm whitespace-pre-wrap break-words">{t("fitnessSetupStart.changed")}</p>;
    if (proposal.tool === "propose_fitness_current_set_edit") return <p className="text-sm whitespace-pre-wrap break-words">{t("fitnessCurrentSetEdit.changed")}</p>;
    if (proposal.tool === "propose_fitness_draft_restore") return <p className="text-sm whitespace-pre-wrap break-words">{t("mirror.fitnessDraftRestore.notRequested")}</p>;
    // Defensive by contract: a proposal may arrive MALFORMED (the model emitted a
    // tool with empty args — observed when a navigation phrase leaks to the model)
    // or be an UNEXPECTED/legacy tool persisted in history (a propose_*_log carries
    // `raw_text`, NOT `content`). NOTHING below may assume a field exists — every
    // string op goes through the guards. A bad proposal renders a safe chip; it can
    // NEVER white-screen the Espelho. (VYV-FRONTEND-1W: `content.length` on undefined.)
    const args = (proposal.args ?? {}) as Record<string, unknown>;
    const str = (v: unknown): string => (typeof v === "string" ? v : "");
    const clamp = (v: string, n: number): string =>
      v.length > n ? `${v.slice(0, n)}…` : v;

    if (proposal.tool === 'propose_fitness_curated_plan') return <p className="text-sm whitespace-pre-wrap break-words">{t('fitnessCuratedPlanAction.reviewIntro')}</p>;
    if (proposal.tool === 'propose_language_class_action') return <p className="text-sm whitespace-pre-wrap break-words">{t('languageClassAction.reviewIntro')}</p>;
    if (proposal.tool === 'prepare_language_tutor_handoff') return <p className="text-sm whitespace-pre-wrap break-words">{t('languageClassAction.tutorIntro')}</p>;
    if (proposal.tool === 'propose_brain_note_action') return <p className="text-sm whitespace-pre-wrap break-words">{t('brainAction.description')}</p>;
    if (proposal.tool === 'prepare_brain_handoff') return <p className="text-sm whitespace-pre-wrap break-words">{t('brainAction.handoffDescription')}</p>;
    if (proposal.tool === 'propose_language_action') return <p className="text-sm whitespace-pre-wrap break-words">{t('languageAction.reviewIntro')}</p>;
    if (proposal.tool === 'propose_nutrition_goals_action') return <p className="text-sm whitespace-pre-wrap break-words">{t('nutritionGoalsAction.reviewIntro')}</p>;
    if (proposal.tool === 'propose_nutrition_hydration_action') return <p className="text-sm whitespace-pre-wrap break-words">{t('nutritionHydrationAction.reviewIntro')}</p>;
    if (proposal.tool === 'propose_nutrition_meal_action') return <p className="text-sm whitespace-pre-wrap break-words">{t('nutritionMealAction.reviewIntro')}</p>;

    if (proposal.tool === "propose_fitness_action") return <p className="text-[13px] text-foreground/85 font-body leading-snug">{t("fitnessAction.action." + str(args.action))}</p>;

    if (proposal.tool === "create_agenda_event") {
      const title = str(args.title);
      const startAt = str(args.start_at);
      const tag = str(args.tag);
      const reminder =
        typeof args.reminder_minutes === "number" ? args.reminder_minutes : null;
      return (
        <div className="flex flex-col gap-1">
          {title && (
            <p className="text-[13px] font-display font-semibold text-foreground leading-snug">
              {title}
            </p>
          )}
          {startAt && (
            <p className="text-[11px] text-foreground/70 font-body">
              {formatDateTime(startAt, lang)}
            </p>
          )}
          {(tag || reminder != null) && (
            <div className="flex items-center gap-2 flex-wrap pt-0.5">
              {tag && (
                <span
                  className="text-[10px] font-display tracking-wide px-2 py-0.5 rounded-full"
                  style={{
                    background: "hsl(187 72% 50% / 0.15)",
                    color: "hsl(187 72% 75%)",
                    border: "1px solid hsl(187 72% 50% / 0.25)",
                  }}
                >
                  {t(`mirror.actions.tag.${tag}`)}
                </span>
              )}
              {reminder != null && (
                <span className="inline-flex items-center gap-1 text-[10px] text-foreground/60 font-body">
                  <Bell size={10} />
                  {interpolate(t("mirror.actions.reminderBefore"), {
                    minutes: reminder,
                  })}
                </span>
              )}
            </div>
          )}
        </div>
      );
    }

    if (["create_habit", "propose_habit_action", "propose_habit_log"].includes(proposal.tool)) return <p className="text-sm whitespace-pre-wrap break-words">{clamp(str(args.name) || str(args.raw_text), 240)}</p>;

    if (proposal.tool === "create_task") {
      const title = str(args.title);
      const notes = str(args.notes);
      return (
        <div className="flex flex-col gap-1">
          <p className="text-[13px] font-display font-semibold text-foreground leading-snug">
            {clamp(title, 60)}
          </p>
          {notes && (
            <p className="text-[10px] text-foreground/55 font-body">
              💬 {t("mirror.actions.hasNotes")}
            </p>
          )}
        </div>
      );
    }

    if (proposal.tool === "create_note") {
      const content = str(args.content);
      const typ = str(args.type) || "idea";
      return (
        <div className="flex flex-col gap-1">
          <span
            className="self-start text-[10px] font-display tracking-wide px-2 py-0.5 rounded-full"
            style={{
              background: "hsl(187 72% 50% / 0.15)",
              color: "hsl(187 72% 75%)",
              border: "1px solid hsl(187 72% 50% / 0.25)",
            }}
          >
            {t(`mirror.actions.noteType.${typ}`)}
          </span>
          {content && (
            <p className="text-[13px] text-foreground/85 font-body leading-snug">
              {clamp(content, 80)}
            </p>
          )}
        </div>
      );
    }

    // UNEXPECTED / legacy / malformed proposal. Show `raw_text` (propose_*_log) or
    // `content` if present, else nothing — the outer chip (type label + buttons)
    // still renders, so it degrades to a generic, harmless card instead of crashing.
    const raw = str(args.raw_text) || str(args.content);
    return raw ? (
      <p className="text-[13px] text-foreground/85 font-body leading-snug">
        {clamp(raw, 80)}
      </p>
    ) : null;
  }, [proposal, t, lang]);

  const handleConfirm = async () => {
    if (submitting || disabled || sleepDisabled) return;
    setSubmitting(true);
    try {
      await onConfirm();
      if (!isReviewedLocalAction) setStatus("created");
    } catch (err) {
      console.warn("[MirrorActionChip] confirm failed:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUndo = async () => {
    if (undoing || !onUndo) return;
    setUndoing(true);
    try {
      await onUndo();
      setStatus("undone");
      setShowUndo(false);
    } catch (err) {
      console.warn("[MirrorActionChip] undo failed:", err);
    } finally {
      setUndoing(false);
    }
  };

  const handleDownload = async () => {
    if (downloading || !onDownload) return;
    setDownloading(true);
    try {
      await onDownload();
    } catch (err) {
      // Handler já superficializa o erro (toast) — aqui só evita unhandled.
      console.warn("[MirrorActionChip] download failed:", err);
    } finally {
      setDownloading(false);
    }
  };

  // Entregável em geração é uma espera REAL (spec+conteúdo+render server-side)
  // — o label genérico "..." vira estado explícito, consistente com a linha
  // "generating" da Biblioteca.
  const isDeliverable = proposal.tool === "propose_deliverable";

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4 }}
      transition={{ duration: 0.22, ease: "easeOut" }}
      className={`rounded-2xl p-3 backdrop-blur-xl ${className ?? ""}`}
      style={{
        background: "hsl(222 35% 9% / 0.65)",
        border: "1px solid hsl(187 72% 50% / 0.22)",
        boxShadow:
          "0 0 20px hsl(187 72% 50% / 0.08), inset 0 1px 0 hsl(255 100% 100% / 0.04)",
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0"
          style={{
            background: "hsl(187 72% 50% / 0.12)",
            border: "1px solid hsl(187 72% 50% / 0.22)",
          }}
        >
          <Icon size={14} style={{ color: "hsl(187 72% 70%)" }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-display tracking-[0.18em] uppercase text-foreground/55 mb-1">
            {t(titleKey(proposal.tool))}
          </p>
          {previewNode}
        </div>
      </div>

      {status === "pending" && (
        <div className="flex gap-2 mt-3">
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting || disabled || sleepDisabled || isRestorePreview || isSetEditPreview || isSetupStartPreview || isSetCompletionPreview || isRestSkipPreview || isSavedPlanPreparePreview || isWorkoutFinishPreview}
            className="flex-1 rounded-xl font-display font-semibold text-[12px] tracking-wide transition-all active:scale-[0.98] disabled:opacity-50"
            style={{
              minHeight: 44,
              background: "hsl(187 72% 50%)",
              color: "hsl(222 38% 6%)",
            }}
          >
            {submitting && isDeliverable ? (
              <span className="inline-flex items-center justify-center gap-1.5">
                <Loader2 size={13} className="animate-spin" />
                {t("mirror.actions.deliverable.generating")}
              </span>
            ) : submitting ? (
              t("common.loading")
            ) : (
              t(isFinanceReview ? 'financeAction.review' : proposal.tool === 'propose_brain_note_action' ? 'noteAction.review' : proposal.tool === 'prepare_brain_handoff' ? 'brainAction.open' : proposal.tool === 'propose_language_class_action' ? 'languageClassAction.review' : proposal.tool === 'prepare_language_tutor_handoff' ? 'languageClassAction.tutorReview' : proposal.tool === 'propose_language_action' ? 'languageAction.review' : proposal.tool === 'propose_fitness_curated_plan' ? 'fitnessCuratedPlanAction.review' : proposal.tool === 'propose_nutrition_goals_action' ? 'nutritionGoalsAction.review' : proposal.tool === 'propose_nutrition_hydration_action' ? 'nutritionHydrationAction.review' : proposal.tool === 'propose_nutrition_meal_action' ? proposal.args?.action === 'delete' ? 'nutritionMealAction.reviewDelete' : 'nutritionMealAction.reviewUpdate' : isFitnessReview ? "fitnessAction.review" : isBodyHealthReview ? "bodyHealthAction.review" : isSleepReview ? "sleepAction.review" : "mirror.actions.confirm")
            )}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="flex-1 rounded-xl font-display font-medium text-[12px] tracking-wide transition-all active:scale-[0.98] disabled:opacity-50"
            style={{
              minHeight: 44,
              background: "hsl(222 18% 18% / 0.6)",
              color: "hsl(210 20% 88%)",
              border: "1px solid hsl(222 18% 30% / 0.6)",
            }}
          >
            {t("mirror.actions.cancel")}
          </button>
        </div>
      )}

      {status === "created" && (
        <div className="flex items-center justify-between mt-3 gap-3">
          <div className="inline-flex items-center gap-1.5 text-[12px] font-display font-semibold" style={{ color: "hsl(142 60% 65%)" }}>
            <Check size={14} />
            {t("mirror.actions.created")}
          </div>
          {onDownload && (
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloading}
              className="inline-flex items-center gap-1.5 px-3 rounded-xl font-display font-semibold text-[11px] tracking-wide transition-all active:scale-[0.98] disabled:opacity-50"
              style={{
                minHeight: 36,
                background: "hsl(187 72% 50% / 0.15)",
                color: "hsl(187 72% 65%)",
                border: "1px solid hsl(187 72% 50% / 0.3)",
              }}
            >
              {downloading ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <Download size={12} />
              )}
              {t("library.download")}
            </button>
          )}
          {showUndo && onUndo && (
            <button
              type="button"
              onClick={handleUndo}
              disabled={undoing}
              className="text-[11px] font-display underline underline-offset-2 text-foreground/65 hover:text-foreground/90 transition-colors disabled:opacity-50"
            >
              {t("mirror.actions.undo")}
            </button>
          )}
        </div>
      )}

      {status === "undone" && (
        <div className="mt-3">
          <p className="text-[12px] font-display font-medium text-foreground/60">
            {t("mirror.actions.undone")}
          </p>
        </div>
      )}

    </motion.div>
  );
};

export default MirrorActionChip;
